import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    LaboratoryDepartmentBreadcrumbs,
    LaboratoryDepartmentCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import { ILaboratoryDepartmentReduxFormState } from "src/reducers/reactReduxForms/laboratoryDepartment";
import * as LaboratoryDepartmentActions from "../../../actions/laboratoryDepartments";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { LaboratoryDepartmentSubMenu } from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./LabDepartmentPage.scss";

interface LabDepartmentPageProps {
    form: Dtos.LaboratoryDepartment & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ILaboratoryDepartmentReduxFormState;
    laboratoryDepartment: Dtos.LaboratoryDepartment;
    lookups: Dtos.Lookup[];
    loadingLaboratoryDepartment: boolean;
    loadLaboratoryDepartmentSuccess: boolean;
    loadLaboratoryDepartmentFailure: boolean;
    loadLaboratoryDepartmentErrorCode: string;
    savingLaboratoryDepartment: boolean;
    saveLaboratoryDepartmentSuccess: boolean;
    saveLaboratoryDepartmentFailure: boolean;
    creatingLaboratoryDepartment: boolean;
    createLaboratoryDepartmentSuccess: boolean;
    createLaboratoryDepartmentFailure: boolean;
    validatingLaboratoryDepartment: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearLaboratoryDepartment: LaboratoryDepartmentActions.ILaboratoryDepartmentClearActionCreator;
    loadLaboratoryDepartment: LaboratoryDepartmentActions.ILaboratoryDepartmentLoadByIdActionCreator;
    saveLaboratoryDepartment: LaboratoryDepartmentActions.ILaboratoryDepartmentSaveActionCreator;
    createLaboratoryDepartment: LaboratoryDepartmentActions.ILaboratoryDepartmentCreateActionCreator;
    validateLaboratoryDepartment: LaboratoryDepartmentActions.ILaboratoryDepartmentValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.laboratoryDepartment";

const LabDepartmentPage: FC<LabDepartmentPageProps> = ({
    form,
    formState,
    formProperties,
    laboratoryDepartment,
    lookups,
    loadingLaboratoryDepartment,
    loadLaboratoryDepartmentFailure,
    loadLaboratoryDepartmentErrorCode,
    savingLaboratoryDepartment,
    saveLaboratoryDepartmentSuccess,
    saveLaboratoryDepartmentFailure,
    creatingLaboratoryDepartment,
    validatingLaboratoryDepartment,
    validationFailures,
    permissions,
    clearLaboratoryDepartment,
    loadLaboratoryDepartment,
    saveLaboratoryDepartment,
    createLaboratoryDepartment,
    validateLaboratoryDepartment,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const { laboratoryDepartmentId } = useParams<{
        laboratoryDepartmentId: string;
    }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);
    const laboratoryDepartmentIdNumber = parseInt(laboratoryDepartmentId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Laboratory Department
    useEffect(() => {
        if (
            laboratoryIdNumber &&
            laboratoryDepartmentIdNumber &&
            !laboratoryDepartment
        ) {
            setDrawerItems(
                LaboratoryDepartmentSubMenu(
                    laboratoryIdNumber,
                    laboratoryDepartmentIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                LaboratoryDepartmentBreadcrumbs(
                    laboratoryIdNumber,
                    laboratoryDepartmentIdNumber
                )
            );

            loadLaboratoryDepartment(laboratoryDepartmentIdNumber);
        } else if (
            laboratoryIdNumber &&
            !laboratoryDepartmentIdNumber &&
            !laboratoryDepartment
        ) {
            setBreadcrumbs(
                LaboratoryDepartmentCreateBreadcrumbs(laboratoryIdNumber)
            );
            setDrawerItems([]);
            createLaboratoryDepartment(laboratoryIdNumber);
        } else if (laboratoryDepartment && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        laboratoryDepartmentIdNumber,
        laboratoryDepartment,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratoryDepartment,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryDepartmentFailure) {
            navigate(`/error/${loadLaboratoryDepartmentErrorCode}`);
        }
    }, [
        loadLaboratoryDepartmentFailure,
        loadLaboratoryDepartmentErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            laboratoryIdNumber &&
            laboratoryDepartmentIdNumber &&
            laboratoryDepartment?.laboratory &&
            laboratoryDepartment?.pathologyDepartment
        ) {
            setBreadcrumbs(
                LaboratoryDepartmentBreadcrumbs(
                    laboratoryIdNumber,
                    laboratoryDepartmentIdNumber,
                    laboratoryDepartment.laboratory?.name,
                    laboratoryDepartment.pathologyDepartment?.name
                )
            );
        }
    }, [
        laboratoryIdNumber,
        laboratoryDepartment,
        laboratoryDepartmentIdNumber,
        setDrawerItems,
        setBreadcrumbs,
        LaboratoryDepartmentBreadcrumbs,
    ]);

    // Reload laboratory department after saving
    useEffect(() => {
        if (!savingLaboratoryDepartment && saveLaboratoryDepartmentSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Laboratory Department saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/labs/${laboratoryIdNumber}/departments`);
            } else if (!laboratoryDepartmentIdNumber) {
                navigate(
                    `/labs/${laboratoryIdNumber}/departments/${laboratoryDepartment?.id}/details`
                );
                clearLaboratoryDepartment();
            } else {
                loadLaboratoryDepartment(laboratoryDepartmentIdNumber);
            }
        }
    }, [
        savingLaboratoryDepartment,
        saveLaboratoryDepartmentSuccess,
        loadLaboratoryDepartment,
        laboratoryDepartmentIdNumber,
        navigate,
        saveAndReturn,
        clearLaboratoryDepartment,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingLaboratoryDepartment && saveLaboratoryDepartmentFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingLaboratoryDepartment,
        saveLaboratoryDepartmentSuccess,
        loadLaboratoryDepartment,
        laboratoryDepartmentIdNumber,
    ]);

    // Cleanup effect to clear the laboratory department on component unmount
    useEffect(() => {
        return () => {
            clearLaboratoryDepartment();
            resetForm(reduxFormName);
        };
    }, [clearLaboratoryDepartment, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.LaboratoryDepartment =
                {} as Dtos.LaboratoryDepartment;

            if (!data.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            await saveLaboratoryDepartment(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.LaboratoryDepartment) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/labs/${laboratoryIdNumber}/departments`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.LaboratoryDepartment =
            {} as Dtos.LaboratoryDepartment;

        loadForm(
            reduxFormName,
            Object.assign({ ...laboratoryDepartment }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.LaboratoryDepartment =
                {} as Dtos.LaboratoryDepartment;

            if (form && hasBeenSubmitted && !validatingLaboratoryDepartment) {
                await validateLaboratoryDepartment(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                laboratoryDepartmentIdNumber,
                permissions,
                Dtos.Permission.LabDepartmentUpdate,
                Dtos.Permission.LabDepartmentCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingLaboratoryDepartment ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row py-4 px-4 align-items-center">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryDepartment",
                                        {
                                            pathologyDepartmentId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "laboratoryDepartment",
                                                                "pathologyDepartmentId",
                                                                lookups
                                                            ),
                                                            form.pathologyDepartmentId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryDepartment",
                                        {
                                            expiryDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "laboratoryDepartment",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        laboratoryDepartmentIdNumber,
        permissions,
        savingLaboratoryDepartment,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabDepartmentView}
            loading={
                loadingLaboratoryDepartment ||
                creatingLaboratoryDepartment ||
                savingLaboratoryDepartment
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<LabDepartmentPageProps> => {
    let laboratoryDepartment: Dtos.LaboratoryDepartment | undefined = !(
        state.laboratoryDepartments.formData instanceof Array
    )
        ? state.laboratoryDepartments.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratoryDepartment: laboratoryDepartment!,
        loadingLaboratoryDepartment:
            state.laboratoryDepartments.loadState &&
            state.laboratoryDepartments.loadState.status ==
                RequestState.Pending,
        loadLaboratoryDepartmentSuccess:
            state.laboratoryDepartments.loadState &&
            state.laboratoryDepartments.loadState.status ==
                RequestState.Success,
        loadLaboratoryDepartmentFailure:
            state.laboratoryDepartments.loadState &&
            state.laboratoryDepartments.loadState.status ==
                RequestState.Failure,
        loadLaboratoryDepartmentErrorCode:
            state.laboratoryDepartments.loadState.errorCode,
        savingLaboratoryDepartment:
            state.laboratoryDepartments.saveState &&
            state.laboratoryDepartments.saveState.status ==
                RequestState.Pending,
        saveLaboratoryDepartmentSuccess:
            state.laboratoryDepartments.saveState &&
            state.laboratoryDepartments.saveState.status ==
                RequestState.Success,
        saveLaboratoryDepartmentFailure:
            state.laboratoryDepartments.saveState &&
            state.laboratoryDepartments.saveState.status ==
                RequestState.Failure,
        creatingLaboratoryDepartment:
            state.laboratoryDepartments.createState &&
            state.laboratoryDepartments.createState.status ==
                RequestState.Pending,
        createLaboratoryDepartmentSuccess:
            state.laboratoryDepartments.createState &&
            state.laboratoryDepartments.createState.status ==
                RequestState.Success,
        createLaboratoryDepartmentFailure:
            state.laboratoryDepartments.createState &&
            state.laboratoryDepartments.createState.status ==
                RequestState.Failure,
        validatingLaboratoryDepartment:
            state.laboratoryDepartments.validateState &&
            state.laboratoryDepartments.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.laboratoryDepartments.lookups,
        form: state.reduxForms.laboratoryDepartment,
        formState: state.laboratoryDepartments.formState,
        formProperties: state.laboratoryDepartments.formProperties,
        reduxFormState: state.reduxForms.formState.laboratoryDepartment,
        validationFailures: laboratoryDepartment
            ? state.laboratoryDepartments.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabDepartmentPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadLaboratoryDepartment: bindActionCreators(
            LaboratoryDepartmentActions.LoadLaboratoryDepartmentById,
            dispatch
        ),
        clearLaboratoryDepartment: bindActionCreators(
            LaboratoryDepartmentActions.Clear,
            dispatch
        ),
        saveLaboratoryDepartment: bindActionCreators(
            LaboratoryDepartmentActions.SaveLaboratoryDepartment,
            dispatch
        ),
        createLaboratoryDepartment: bindActionCreators(
            LaboratoryDepartmentActions.CreateLaboratoryDepartment,
            dispatch
        ),
        validateLaboratoryDepartment: bindActionCreators(
            LaboratoryDepartmentActions.ValidateLaboratoryDepartment,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LabDepartmentPage);
